"use client";

import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useLocation } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { CheckIcon, Loader2, Sparkles, XIcon } from "lucide-react";

import { useOnboardingStore } from "../../lib/onboarding";
import { coachPersonas } from "../../lib/personas";
import { completeOnboarding, type CompleteOnboardingPayload } from "../../lib/api";
import { computeCoachRecommendations, EMPATHEAR_ID } from "../../lib/recommendations";
import { Input } from "@/components/ui/input";
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import type { CoachPersona } from "../../lib/personas";

const supportNeedOptions = [
  "I want to get better results from my work.",
  "I want clarity about my next career step / career path",
  "I feel overwhelmed and want more balance in my life / understand what really matters to me.",
  "I want to show up better as a leader",
  "I am going through a professional change (new job / role / project) and feel unsure.",
  "I just need to talk and feel heard.",
  "Other",
] as const;

type SupportNeedOption = (typeof supportNeedOptions)[number];

const OnboardingStep6 = () => {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const {
    displayName,
    role,
    experience,
    industry,
    goalsText,
    supportNeed,
    recommendedCoach,
    selectedCoach,
    phoneNumber,
    timezone,
    setSupportNeed,
    setRecommendedCoach,
    setSelectedCoach,
    setPhoneNumber,
    setTimezone,
    reset,
  } = useOnboardingStore();

  const [touched, setTouched] = useState(false);
  const [hasManualCoachSelection, setHasManualCoachSelection] = useState(false);
  const [selectedSupportNeed, setSelectedSupportNeed] = useState<SupportNeedOption | "">("");
  const [customSupportNeed, setCustomSupportNeed] = useState("");
  const [isCoachModalOpen, setIsCoachModalOpen] = useState(false);
  const [viewingCoach, setViewingCoach] = useState<CoachPersona | null>(null);

  const recommendationOrder = useMemo(() => {
    return computeCoachRecommendations({ role, experience, industry, supportNeed });
  }, [role, experience, industry, supportNeed]);

  const personaMap = useMemo(() => {
    return coachPersonas.reduce<Record<string, (typeof coachPersonas)[number]>>((acc, persona) => {
      acc[persona.id] = persona;
      return acc;
    }, {});
  }, []);

  const primaryRecommendation = useMemo(() => {
    if (recommendedCoach) {
      return recommendedCoach;
    }
    return recommendationOrder[0] ?? EMPATHEAR_ID;
  }, [recommendedCoach, recommendationOrder]);

  useEffect(() => {
    if (!supportNeed) {
      setSelectedSupportNeed("");
      setCustomSupportNeed("");
      return;
    }

    if (supportNeedOptions.includes(supportNeed as SupportNeedOption)) {
      setSelectedSupportNeed(supportNeed as SupportNeedOption);
      setCustomSupportNeed("");
      return;
    }

    setSelectedSupportNeed("Other");
    setCustomSupportNeed(supportNeed);
  }, [supportNeed]);

  useEffect(() => {
    if (!supportNeed) {
      return;
    }

    const topCoach = recommendationOrder[0] ?? EMPATHEAR_ID;
    if (topCoach && topCoach !== recommendedCoach) {
      setRecommendedCoach(topCoach);
    }

    if (!selectedCoach || !hasManualCoachSelection) {
      setSelectedCoach(topCoach);
    }
  }, [supportNeed, recommendationOrder, recommendedCoach, selectedCoach, hasManualCoachSelection, setRecommendedCoach, setSelectedCoach]);

  const sortedCoachIds = useMemo(() => {
    const orderIndex = new Map<string, number>();
    recommendationOrder.forEach((id, index) => orderIndex.set(id, index));

    return [...coachPersonas]
      .sort((a, b) => (orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER))
      .map((persona) => persona.id);
  }, [recommendationOrder]);

  const supportNeedError =
    touched && (!selectedSupportNeed || (selectedSupportNeed === "Other" && !customSupportNeed.trim()));
  const coachSelectionError = touched && !!supportNeed && !selectedCoach;

  const mutation = useMutation({
    mutationFn: async (payload: CompleteOnboardingPayload) => {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing authentication token");
      }
      return completeOnboarding(payload, token);
    },
    onSuccess: async (data) => {
      console.log('Onboarding completed successfully:', data);
      // Force invalidate the current user query to refresh the profile and wait for it to complete
      await queryClient.invalidateQueries({ queryKey: ["current-user"] });
      
      // Reset the onboarding store
      reset();
      
      // Now that the query is invalidated and likely refetched, we can redirect
      console.log('Redirecting to dashboard after onboarding completion');
      setLocation("/dashboard");
    },
    onError: (error) => {
      console.error('Error completing onboarding:', error);
    },
  });

  const handleSupportNeedSelect = (option: SupportNeedOption) => {
    setTouched(false);
    setSelectedSupportNeed(option);
    setHasManualCoachSelection(false);

    if (option === "Other") {
      setSupportNeed(customSupportNeed.trim());
      return;
    }

    setCustomSupportNeed("");
    setSupportNeed(option);
  };

  const handleCustomSupportNeedChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomSupportNeed(value);
    setSupportNeed(value.trim());
  };

  const handleCoachSelect = (coachId: string) => {
    setSelectedCoach(coachId);
    setHasManualCoachSelection(true);
    setIsCoachModalOpen(false);
    setViewingCoach(null);
  };

  const openCoachModal = (coach: CoachPersona) => {
    setViewingCoach(coach);
    setIsCoachModalOpen(true);
  };

  const phoneError = touched && !!phoneNumber && !/^\+?[1-9]\d{1,14}$/.test(phoneNumber.replace(/\s/g, ''));

  const handleFinish = () => {
    setTouched(true);
    const trimmedGoals = goalsText.trim();
    const finalSupportNeedValue =
      selectedSupportNeed === "Other" ? customSupportNeed.trim() : supportNeed;

    if (selectedSupportNeed === "Other") {
      setSupportNeed(finalSupportNeedValue);
    }

    const finalSupportNeed = finalSupportNeedValue;
    const finalSelectedCoach = selectedCoach ?? primaryRecommendation;
    const finalRecommendedCoach = primaryRecommendation;

    if (!displayName || !role || !experience || !industry || !trimmedGoals || !finalSupportNeed || !finalSelectedCoach || !finalRecommendedCoach) {
      return;
    }

    const payload: CompleteOnboardingPayload & { phoneNumber: string, timezone: string } = {
      displayName,
      role,
      experience,
      industry,
      goalStatement: trimmedGoals,
      supportNeed: finalSupportNeed,
      recommendedCoach: finalRecommendedCoach,
      selectedCoach: finalSelectedCoach,
      phoneNumber,
      timezone,
    };

    mutation.mutate(payload);
  };

  return (
    <>
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 font-display">Where do you need support?</h2>
          <p className="mt-2 text-slate-600"><span className="underline">Choose the one coaching path that speaks to you most</span>. We’ll match you with a lead coach to guide the way, while EmpathEAR stays by your side for emotional support.</p>
        </div>

        <div className="space-y-3">
          <span className="block text-sm font-medium text-slate-700 font-sans">Primary coaching focus *</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {supportNeedOptions.map((option) => {
              const isSelected = selectedSupportNeed === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleSupportNeedSelect(option)}
                  className={`text-left p-4 border rounded-lg transition-all duration-200 font-sans focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    ${isSelected ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-white text-slate-700 hover:border-slate-300 hover:bg-accent/5"}`}
                >
                  {option}
                </button>
              );
            })}
          </div>
          {supportNeedError && (
            <p className="text-sm text-red-600">Select at least one focus area to continue.</p>
          )}
        </div>

        {selectedSupportNeed === "Other" && (
          <div className="space-y-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4">
            <label htmlFor="custom-support-need" className="text-sm font-medium text-slate-700 font-sans">Describe the focus you have in mind</label>
            <Input
              id="custom-support-need"
              value={customSupportNeed}
              onChange={handleCustomSupportNeedChange}
              placeholder="e.g. Balancing startup chaos with wellbeing, designing org structure post-series B"
            />
          </div>
        )}

        {supportNeed && selectedSupportNeed !== "Other" && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">Recommended lead coach</p>
                <h3 className="mt-1 text-xl font-display font-semibold text-slate-900">
                  {personaMap[primaryRecommendation]?.name ?? "Your Coach"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Based on your profile and focus, we suggest starting with {personaMap[primaryRecommendation]?.name}. EmpathEAR (Arjun) stays in your corner for deep listening anytime.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm font-medium text-slate-700 font-sans">Choose your starting coach *</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedCoachIds.map((coachId) => {
              const persona = personaMap[coachId];
              if (!persona) return null;
              const isSelected = selectedCoach === coachId;
              const isRecommended = primaryRecommendation === coachId;
              const isEmpathEar = coachId === EMPATHEAR_ID;

              return (
                <button
                  key={coachId}
                  type="button"
                  onClick={() => openCoachModal(persona)}
                  className={`relative flex flex-col items-center rounded-xl border p-5 text-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    ${isSelected ? "border-primary bg-primary/10 shadow" : "border-slate-200 bg-white hover:border-primary/60 hover:shadow"}`}
                >
                  <img src={`/coach-avatars/${persona.avatar}`} alt={persona.name} className="h-20 w-20 rounded-full border border-white shadow" />
                  <h4 className="mt-4 text-lg font-display font-semibold text-slate-900">{persona.name}</h4>
                  <p className="text-sm text-slate-600 font-sans">{persona.title}</p>
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {isRecommended && (
                      <span className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                        <Sparkles className="h-3 w-3" /> Recommended
                      </span>
                    )}
                    {isEmpathEar && (
                      <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">EmpathEAR</span>
                    )}
                    {isSelected && (
                      <span className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold">
                        <CheckIcon className="h-3 w-3" /> Selected
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {coachSelectionError && (
            <p className="text-sm text-red-600">Choose the coach you want to launch with. You can always change this later.</p>
          )}
        </div>


        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={() => setLocation("/onboarding/step-6")}
            className="inline-flex items-center justify-center px-6 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary font-display"
            disabled={mutation.isPending}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleFinish}
            disabled={mutation.isPending}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm bg-primary !text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary disabled:opacity-60 font-display"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Saving
              </>
            ) : (
              "Finish & Go to Dashboard"
            )}
          </button>
        </div>

        {mutation.isError && (
          <p className="text-sm text-red-600 text-center">{(mutation.error as Error)?.message ?? "Something went wrong. Please try again."}</p>
        )}
      </div>

      {viewingCoach && (
        <Modal open={isCoachModalOpen} onOpenChange={setIsCoachModalOpen}>
          <ModalContent className="max-w-2xl">
            <ModalHeader>
              <div className="flex items-start gap-4">
                <img src={`/coach-avatars/${viewingCoach.avatar}`} alt={viewingCoach.name} className="h-24 w-24 rounded-full border-2 border-white shadow-lg" />
                <div className="flex-1 pt-2">
                  <ModalTitle className="text-2xl font-display tracking-tight">{viewingCoach.name}</ModalTitle>
                  <ModalDescription className="text-base text-primary font-semibold">{viewingCoach.title}</ModalDescription>
                  <p className="mt-2 text-sm text-slate-600">{viewingCoach.bio}</p>
                </div>
              </div>
            </ModalHeader>
            <div className="px-6 pb-6">
              <div className="mt-4 flex flex-wrap gap-2">
                {viewingCoach.highlightTags?.map(tag => (
                  <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <ModalFooter>
              <Button variant="ghost" onClick={() => setIsCoachModalOpen(false)}>Cancel</Button>
              <Button onClick={() => handleCoachSelect(viewingCoach.id)} className="!text-white">Select {viewingCoach.name}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default OnboardingStep6;
